// The deployment profile read the admin SPA makes, on the `HttpClient` with the browser session
// cookie: the environment this deployment runs in and the ecosystems it serves, decoded closed with
// the admin contract. A refusal keeps its status, read before the body, so the screen tells an
// expired session from a deployment without operator auth, and the error code and trace id its body
// names, to quote to support.
import { Duration, Effect, Match, Option, Schema } from 'effect';
import { FetchHttpClient, HttpClient, HttpClientError, HttpClientRequest } from 'effect/http';
import { PowerDeploymentProfile } from '@finnest/power-admin-contracts';
import { PowerRefusal, powerRefusalOf } from '@finnest/power-admin-contracts/failure';

export const DEPLOYMENT_PROFILE_PATH = '/power/admin/deployment';

/** The query key of the profile: one per deployment, whatever the screen's scope. */
export const deploymentQueryKeys = { profile: ['power-admin', 'deployment'] as const };

/**
 * Power refused the read with a non-2xx status, such as 401 for an expired session; `refusal` is
 * the error code and trace id its body names, if it names a code.
 */
export class DeploymentProfileRefused extends Schema.TaggedError<DeploymentProfileRefused>()(
  'DeploymentProfileRefused',
  {
    status: Schema.Int,
    /** The error code and trace id the answer names; none when its body names no code or is lost. */
    refusal: Schema.NullOr(PowerRefusal),
  },
) {
  override get message(): string {
    return `deployment profile refused with HTTP ${String(this.status)}`;
  }
}

/** No answer: the connection failed or the body stopped in transit. */
export class DeploymentProfileUnreachable extends Schema.TaggedError<DeploymentProfileUnreachable>()(
  'DeploymentProfileUnreachable',
  { cause: Schema.instanceOf(HttpClientError.HttpClientError) },
) {
  override get message(): string {
    return 'deployment profile request failed';
  }
}

/** A 2xx answer whose body is not the profile contract. */
export class DeploymentProfileReplyInvalid extends Schema.TaggedError<DeploymentProfileReplyInvalid>()(
  'DeploymentProfileReplyInvalid',
  { cause: Schema.instanceOf(Schema.SchemaError) },
) {
  override get message(): string {
    return 'deployment profile answered outside the contract';
  }
}

/** Power gave no answer inside the read budget. */
export class DeploymentProfileTimedOut extends Schema.TaggedError<DeploymentProfileTimedOut>()(
  'DeploymentProfileTimedOut',
  {},
) {
  override get message(): string {
    return 'deployment profile read timed out';
  }
}

/** Why the profile could not be read. */
export class DeploymentProfileReadError extends Schema.TaggedError<DeploymentProfileReadError>()(
  'DeploymentProfileReadError',
  {
    reason: Schema.Union([
      DeploymentProfileRefused,
      DeploymentProfileUnreachable,
      DeploymentProfileReplyInvalid,
      DeploymentProfileTimedOut,
    ]),
  },
) {
  override readonly cause = this.reason;
  override get message(): string {
    return `deployment profile read failed: ${this.reason._tag}`;
  }
}

// A read never waits longer than this.
const READ_BUDGET_SECONDS = 15;
const READ_BUDGET = Duration.seconds(READ_BUDGET_SECONDS);
const HTTP_SUCCESS_FLOOR = 200;
const HTTP_SUCCESS_CEILING = 299;
const CLOSED = { onExcessProperty: 'error' } as const;

const failed = (reason: DeploymentProfileReadError['reason']): DeploymentProfileReadError =>
  DeploymentProfileReadError.make({ reason });

const unreachable = (cause: HttpClientError.HttpClientError): DeploymentProfileReadError =>
  failed(DeploymentProfileUnreachable.make({ cause }));

// The read's `finnest.outcome`, one per way it ends.
const OUTCOME_OF_REASON = {
  DeploymentProfileRefused: 'refused',
  DeploymentProfileUnreachable: 'unreachable',
  DeploymentProfileReplyInvalid: 'invalid-reply',
  DeploymentProfileTimedOut: 'timed-out',
} as const satisfies Record<DeploymentProfileReadError['reason']['_tag'], string>;

type ReadOutcome = 'read' | (typeof OUTCOME_OF_REASON)[keyof typeof OUTCOME_OF_REASON];

const outcome = (value: ReadOutcome): Effect.Effect<void> =>
  Effect.annotateCurrentSpan('finnest.outcome', value);

const decodeProfile = Schema.decodeEffect(Schema.fromJsonString(PowerDeploymentProfile));

/** The deployment profile of the Power API at `apiBaseUrl`. */
export const fetchDeploymentProfile = Effect.fn('power-admin.deployment.read-profile')(
  function* (
    apiBaseUrl: string,
  ): Effect.fn.Return<PowerDeploymentProfile, DeploymentProfileReadError, HttpClient.HttpClient> {
    const client = yield* HttpClient.HttpClient;
    // `prependUrl` joins the base and the path with one slash, whether or not the base ends in one.
    const request = HttpClientRequest.prependUrl(
      HttpClientRequest.get(DEPLOYMENT_PROFILE_PATH),
      apiBaseUrl,
    );
    const response = yield* client
      .execute(HttpClientRequest.acceptJson(request))
      .pipe(Effect.mapError(unreachable));
    if (response.status < HTTP_SUCCESS_FLOOR || response.status > HTTP_SUCCESS_CEILING) {
      // The status already says why; a body lost in transit only loses what to quote to support.
      const refusal = yield* response.text.pipe(
        Effect.map(powerRefusalOf),
        Effect.orElseSucceed(() => Option.none()),
      );
      return yield* failed(
        DeploymentProfileRefused.make({
          status: response.status,
          refusal: Option.getOrNull(refusal),
        }),
      );
    }
    const text = yield* response.text.pipe(Effect.mapError(unreachable));
    return yield* decodeProfile(text, CLOSED).pipe(
      Effect.mapError((cause) => failed(DeploymentProfileReplyInvalid.make({ cause }))),
    );
  },
  Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' }),
  Effect.provideService(HttpClient.TracerPropagationEnabled, false),
  Effect.timeoutOrElse({
    duration: READ_BUDGET,
    orElse: () => Effect.fail(failed(DeploymentProfileTimedOut.make())),
  }),
  Effect.tap(() => outcome('read')),
  Effect.tapError((failure: DeploymentProfileReadError) =>
    outcome(OUTCOME_OF_REASON[failure.reason._tag]),
  ),
);

/**
 * The error code and trace id a failed profile read names, to quote to support; none when Power
 * did not answer, timed out or answered outside the contract.
 */
export const failureRefusal = (failure: DeploymentProfileReadError): Option.Option<PowerRefusal> =>
  Match.valueTags(failure.reason, {
    DeploymentProfileRefused: ({ refusal }) => Option.fromNullOr(refusal),
    DeploymentProfileUnreachable: () => Option.none(),
    DeploymentProfileReplyInvalid: () => Option.none(),
    DeploymentProfileTimedOut: () => Option.none(),
  });
